import React, { useEffect, useReducer, useState } from 'react'
import Map from './Map'
import SimpleGoogleMap from './SimpleGoogleMap'
import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'
import ClustersList from './ClustersList'
import InfoWindowContainer from './InfoWindowContainer'
import { useClustersQuery } from '../graphql/hooks/clusters'
import { TopPanel } from './TopPanel'
import { Pharmacy } from '../types/pharmacy'
import { ClusterHull } from '../types/cluster'
import { usePharmaciesClustersHullsQuery } from '../graphql/hooks/pharmacies'
import { useClickToSelect, mergeRefs, Selection } from '../utils/utils'
import FilterDropdown from '../ui-components/FilterOptions'
import {
  filterMapReducer,
  initialMapFilterState,
} from '../contexts/mapFilterReducer'
import { usePharmacyMapData } from '../contexts/usePharmacyMapData'

interface Props {
  setSelectedPharmacy: (id: string | undefined) => void
  selectedPharmacy: string | undefined
}

const MapView: React.FC<Props> = ({ setSelectedPharmacy, selectedPharmacy }) => {
  const [filterState, dispatchFilterState] = useReducer(
    filterMapReducer,
    initialMapFilterState
  )
  const [isWithDeleted, setIsWithDeleted] = useState(false)

  const { clusters: serverClusters } = usePharmacyMapData({
    selectedProjects: filterState.selectedProjects,
    filterByOption: filterState.filterByOption,
    searchTerm: filterState.searchTerm,
    onlyWithScheduledAppointments: filterState.onlyWithScheduledAppointments,
    includesTerminatedProjects: filterState.includesTerminatedProjects,
    isAvailableForCallcenter: filterState.isAvailableForCallcenter,
    isOnlyPriority: filterState.isOnlyPriority,
    isOpenPotential: filterState.isOpenPotential,
    isWithDeleted,
  })

  const { data: clusters } = useClustersQuery(isWithDeleted)
  const [pharmacyArray, setPharmacyArray] = useState<Pharmacy[]>([])
  const [hullsArray, setHullsArray] = useState<ClusterHull[]>([])
  const [showClusters, setShowClusters] = useState<string[]>([])

  useEffect(() => {
    if (!clusters || clusters.length === 0) return

    const clusterIds = clusters.map((item) => item.id)

    setShowClusters((prev) => {

      if (prev.length === 0) {
        return clusterIds
      }

      const validIds = new Set(clusterIds)
      const stillExisting = prev.filter((id) => validIds.has(id))

      return stillExisting
    })
  }, [clusters])

  const { data: hulls } = usePharmaciesClustersHullsQuery(showClusters, isWithDeleted)

  useEffect(() => {
    const visible = new Set(showClusters)
    const filteredClusters = (serverClusters ?? []).filter(c => visible.has(c.id))

    const nextPharmacies =
      filteredClusters
        .flatMap(c => c?.pharmacies ?? [])
        .filter((p: Pharmacy | undefined): p is Pharmacy => !!p)
        .filter(
          (p) =>
            typeof p?.address?.latitude === 'number' &&
            typeof p?.address?.longitude === 'number'
        )

    setPharmacyArray(nextPharmacies)

    const nextHulls =
      (hulls ?? [])
        .filter(Boolean)
        .filter((h: any) => visible.has(h.id)) as ClusterHull[]

    setHullsArray(nextHulls)
  }, [serverClusters, showClusters, hulls])

  const [viewMode, setViewMode] = useState<'map' | 'pharmacy-pins'>('pharmacy-pins')
  const [isExpandable, setIsExpandable] = useState(false)
  const [isScreenshotEnabled, setIsScreenshotEnabled] = useState(false)
  const containerRef = React.useRef<HTMLDivElement | null>(null)
  const [clickToSelectRef, dx, dy, startX, startY, selection] = useClickToSelect()
  const containerMergedRef = mergeRefs([containerRef, clickToSelectRef])

  const onCloseInfoWindow = () => setSelectedPharmacy(undefined)

  const changeColor = (id: string, color: string) => {
    const preparedPharmacies = pharmacyArray.map((pharmacy) => {
      if (pharmacy.id !== id) {
        return pharmacy
      } else {
        return {
          ...pharmacy,
          colorcode: color
        }
      }
    })
    setPharmacyArray(preparedPharmacies)
  }

  React.useEffect(() => {
    if (!isScreenshotEnabled) return
    const container = containerRef.current
    if (!container || selection !== Selection.Selected || dx === 0 || dy === 0) return

    html2canvas(container, {
      x: startX,
      y: startY,
      width: dx,
      height: dy,
      useCORS: true,
    }).then((canvas: any) => {
      const imgData = canvas.toDataURL('image/png')
      const pdf = new jsPDF({
        orientation: dx > dy ? 'landscape' : 'portrait',
        unit: 'px',
        format: [dx, dy],
      })
      pdf.addImage(imgData, 'PNG', 0, 0, dx, dy)
      pdf.save('screenshot.pdf')
    })
  }, [dx, dy, startX, startY, selection, isScreenshotEnabled])

  const areProjectsSelected = filterState.selectedProjects.length > 0

  return (
    <div className="container-box" ref={containerMergedRef}>
      {selection === Selection.Dragging && isScreenshotEnabled && (
        <div
          className="selection"
          style={{
            transform: `translate(${startX}px, ${startY}px)`,
            width: `${dx}px`,
            height: `${dy}px`,
          }}
        />
      )}

      {isExpandable &&       
      <FilterDropdown
        filterState={filterState}
        dispatchFilterState={dispatchFilterState}
        areProjectsSelected={areProjectsSelected}
        isWithDeleted={isWithDeleted}
        setIsWithDeleted={setIsWithDeleted}
      />
      }

      <>
        {viewMode === 'map' && (
          <SimpleGoogleMap hulls={hullsArray} isScreenshot={isScreenshotEnabled}>
            <Map pharmacies={pharmacyArray} onClick={setSelectedPharmacy} showPins={false}>
              <InfoWindowContainer
                onClose={onCloseInfoWindow}
                selectedPharmacy={selectedPharmacy}
                pharmacies={pharmacyArray}
                changeColor={changeColor}
                isWithDeleted={isWithDeleted}
              />
              <div className="fixed mt-0 mx-2 p-3 mt-2 right-0 w-1/6 bg-white rounded-md">
                <TopPanel
                  viewMode={viewMode}
                  activateMapView={() => setViewMode('map')}
                  activatePinsView={() => setViewMode('pharmacy-pins')}
                  setIsExpandable={setIsExpandable}
                  isScreenshot={isScreenshotEnabled}
                  isExpandable={isExpandable}
                  setIsScreenshot={setIsScreenshotEnabled}
                />
              </div>
              <div className="fixed bottom-0 m-2 mr-20 p-2 pr-12 left-0 w-auto overflow-x-auto h-144px flex justify-start bg-white rounded-md">
                <ClustersList showClusters={showClusters} setShowClusters={setShowClusters} isWithDeleted={isWithDeleted}/>
              </div>
            </Map>
          </SimpleGoogleMap>
        )}

        {viewMode === 'pharmacy-pins' && (
          <SimpleGoogleMap isScreenshot={isScreenshotEnabled}>
            <Map pharmacies={pharmacyArray} onClick={setSelectedPharmacy} showPins={viewMode === 'pharmacy-pins'}>
              <InfoWindowContainer
                onClose={onCloseInfoWindow}
                selectedPharmacy={selectedPharmacy}
                pharmacies={pharmacyArray}
                changeColor={changeColor}
                isWithDeleted={isWithDeleted}
              />
              <div className="fixed mt-0 mx-2 p-3 mt-2 right-0 w-1/6 bg-white rounded-md">
                <TopPanel
                  viewMode={viewMode}
                  activateMapView={() => setViewMode('map')}
                  activatePinsView={() => setViewMode('pharmacy-pins')}
                  setIsExpandable={setIsExpandable}
                  isExpandable={isExpandable}
                  isScreenshot={isScreenshotEnabled}
                  setIsScreenshot={setIsScreenshotEnabled}
                />
              </div>
              <div className="fixed bottom-0 m-2 mr-20 p-2 pr-12 left-0 w-auto overflow-x-auto h-144px flex justify-start bg-white rounded-md">
                <ClustersList showClusters={showClusters} setShowClusters={setShowClusters} isWithDeleted={isWithDeleted}/>
              </div>
            </Map>
          </SimpleGoogleMap>
        )}
      </>
    </div>
  )
}

export default MapView
